import { Autocomplete, FormHelperText, TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import {
  useDropdownSourceContext,
  useHubRideActionConfirm,
  usePageContext,
  useRidesBulkActions,
} from "@ryde/redux";
import {
  bulkUpdateStationsDrawerAtom,
  HubRideActionKind,
  IDropdownItem,
  Log,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
} from "@ryde/shared";

import { BulkUpdateStationsWrapperStyled } from "./parts";

export const BulkUpdateStationsRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);

  const setBulkUpdateStationsDrawerOpened = useSetRecoilState(
    bulkUpdateStationsDrawerAtom,
  );

  const { getDropdownItems } = useDropdownSourceContext();
  const { bulkUpdateStations } = useRidesBulkActions();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();

  const [stationItems, setStationItems] = useState<IDropdownItem[]>([]);
  const [originStation, setOriginStation] = useState<IDropdownItem | null>(
    null,
  );
  const [destinationStation, setDestinationStation] =
    useState<IDropdownItem | null>(null);

  useEffect(() => {
    const fetchStations = async (): Promise<void> => {
      const items = await getDropdownItems?.("station");
      setStationItems(items || []);
    };

    fetchStations().catch(error =>
      Log.error(t("errors.failedToFetchStations"), error),
    );
  }, [getDropdownItems, t]);

  const onCloseDrawer = useEvent((): void => {
    setBulkUpdateStationsDrawerOpened(false);
  });

  const isSubmitDisabled = !originStation && !destinationStation;

  const onSaveClick = useEvent(async (): Promise<void> => {
    if (isSubmitDisabled) {
      return;
    }

    const isHubModifyConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubModifyConfirmed) {
      return;
    }

    const result = await bulkUpdateStations({
      ids: selectedRideIds,
      firstStationId: originStation ? Number(originStation.value) : undefined,
      lastStationId: destinationStation
        ? Number(destinationStation.value)
        : undefined,
    });

    if (result?.success) {
      onCloseDrawer();
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <FormDrawer
        onCloseDrawer={onCloseDrawer}
        onCancelClick={onCloseDrawer}
        title={t("general.UPDATE_STATIONS")}
        onSaveClick={onSaveClick}
        footerSaveLabel={t("general.NEXT")}
        isSaveDisabled={isSubmitDisabled}
      >
        <BulkUpdateStationsWrapperStyled>
          <FormHelperText>{t("general.UPDATE_STATIONS_HELPER")}</FormHelperText>

          <Autocomplete
            data-e2e="bulk-update-stations-origin"
            options={stationItems}
            value={originStation}
            onChange={(_, value) => setOriginStation(value)}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={params => (
              <TextField
                {...params}
                label={t("ride.formLabel.originStationId")}
              />
            )}
          />

          <Autocomplete
            data-e2e="bulk-update-stations-destination"
            options={stationItems}
            value={destinationStation}
            onChange={(_, value) => setDestinationStation(value)}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={params => (
              <TextField
                {...params}
                label={t("ride.formLabel.destinationStationId")}
              />
            )}
          />
        </BulkUpdateStationsWrapperStyled>
      </FormDrawer>
    </BaseDrawer>
  );
};
